import { HearingPhaseBarDirective } from '../hearing-detail/hearing-phase-bar.directive';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import { SdmfAuditComponent } from './sdmf-audit.component';
import type { SdmfAuditRead } from './sdmf-audit.models';

import { RoleService } from '@app/shared/services/roles.service';
import { createTerminologyUsageSignals } from '@app/shared/terminology/terminology-usage-signals';
import { PHMS_OPERATIONAL_TERMINOLOGY } from '../phms-operational.terminology';
import { SdmfAnalysisService } from './sdmf-analysis.service';
import { SdmfAnalysisEditorComponent } from './sdmf-analysis-editor.component';
import {
  SDMF_FACTORS,
  SdmfAnalysisDto,
  SdmfAnalysisUpsertRequest,
  SdmfFactorRatingInput,
  SdmfFactorScale,
  sdmfScaleOptions,
  sdmfScalePrompt,
  sdmfCompletionIssues,
} from './sdmf-analysis.models';

/**
 * Author permission for the SDMF analysis save. The backend (`PhmsSdmfAnalysisEndpoints`) gates the
 * PUT on the same existing proceeding-scoped record-author permission as the pre-hearing capture;
 * the surface mirrors that so the read-only state matches the server.
 */
const SDMF_AUTHOR_PERMISSION = 'inmate_offense_record.author';

/**
 * Pre-hearing SDMF analysis section (#1446), hosted inside the pre-hearing capture surface.
 *
 * A Structured Decision-Making Framework form modeled on the BITS SDMF per
 * `docs/features/sdmf-migration-to-phms-plan.md`: each fixed decision factor takes a structured
 * rating (radio: aggravating / neutral / mitigating) plus an optional note, accompanied by a summary
 * narrative. Saving is an explicit whole-form PUT that creates the analysis on first save; the saved
 * analysis is re-hydrated from the server response (read-back) and re-loaded on revisit, with a
 * saved analysis reference whenever a saved record exists.
 */
@Component({
  selector: 'phms-sdmf-analysis-section',
  standalone: true,
  imports: [
    HearingPhaseBarDirective,
    CommonModule,
    SdmfAnalysisEditorComponent,
    SdmfAuditComponent,
  ],
  templateUrl: './sdmf-analysis-section.component.html',
  styleUrl: './sdmf-analysis-section.component.scss',
})
export class SdmfAnalysisSectionComponent {
  readonly operationalText = createTerminologyUsageSignals(
    PHMS_OPERATIONAL_TERMINOLOGY.sdmf,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly roles = inject(RoleService);
  private readonly analysisService = inject(SdmfAnalysisService);

  readonly auditRead: SdmfAuditRead = (operation, parameters, signal) => {
    const id = this._proceedingId();
    if (!id || signal.aborted)
      return Promise.reject(new Error('Analysis source unavailable.'));
    return firstValueFrom(
      this.analysisService
        .audit(id, operation, parameters)
        .pipe(takeUntil(fromEvent(signal, 'abort'))),
    );
  };
  auditSourceKey() {
    return `${this._proceedingId()}/${this.savedAnalysis()?.revision ?? 0}`;
  }
  /** The hosting proceeding. Setting it (re)loads any saved analysis. */
  @Input({ required: true })
  set scheduledProceedingId(value: number | null) {
    if (value === this._proceedingId()) return;
    this._proceedingId.set(value);
    this.loadGeneration += 1;
    this.resetDraft();
    if (value !== null && value > 0) {
      this.load(value);
    }
  }
  get scheduledProceedingId(): number | null {
    return this._proceedingId();
  }

  private readonly _proceedingId = signal<number | null>(null);

  private loadGeneration = 0;
  readonly loadError = signal<string | null>(null);
  readonly isLoaded = signal(false);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly lastSavedAt = signal<Date | null>(null);
  readonly dirty = signal(false);
  readonly stale = signal(false);
  readonly uncertain = signal(false);
  private saveIntent: {
    key: string;
    payload: SdmfAnalysisUpsertRequest;
  } | null = null;
  private certificationIntent: {
    key: string;
    operation: 'complete' | 'reopen';
    revision: number;
  } | null = null;

  /** The saved analysis record, when one exists — drives the saved analysis reference. */
  readonly savedAnalysis = signal<SdmfAnalysisDto | null>(null);

  /** Author permission gate; the form is read-only (save suppressed) when this is false. */
  private readonly authorPermission = this.roles.hasAnyPermissionSignal(
    SDMF_AUTHOR_PERMISSION,
  );
  private readonly captureEnabled = signal(true);
  @Input() set authoringEnabled(value: boolean) {
    this.captureEnabled.set(value);
  }
  readonly canAuthor = computed(
    () => this.authorPermission() && this.captureEnabled(),
  );

  /** True while the form is editable: actor can author and the proceeding is resolved. */
  readonly canEdit = computed(
    () =>
      this.canAuthor() &&
      !this.uncertain() &&
      !this.stale() &&
      !this.savedAnalysis()?.isCertified &&
      this.isLoaded() &&
      !this.isLoading() &&
      !this.isSaving() &&
      !this.loadError() &&
      (this._proceedingId() ?? 0) > 0,
  );

  /* --- Form state (signals so the save gate recomputes). --- */
  readonly ratings = signal<Record<string, string>>({});
  readonly notes = signal<Record<string, string>>({});
  readonly summaryNarrative = signal('');

  /** Fixed factor/rating config the template renders. */
  readonly factors = SDMF_FACTORS;
  readonly scaleOptions = sdmfScaleOptions;
  readonly scalePrompt = sdmfScalePrompt;

  /** Per-category expansion (the BITS "›" chevron): expanded rows show the notes body. */
  readonly expanded = signal<Record<string, boolean>>({});

  isExpanded(factorCode: string): boolean {
    return this.expanded()[factorCode] === true;
  }

  toggleExpanded(factorCode: string): void {
    this.expanded.update((current) => ({
      ...current,
      [factorCode]: !current[factorCode],
    }));
  }

  expandAll(): void {
    const all: Record<string, boolean> = {};
    for (const factor of this.factors) {
      all[factor.code] = true;
    }
    this.expanded.set(all);
  }

  collapseAll(): void {
    this.expanded.set({});
  }

  /**
   * The latest CRA level annotation on the CRA row (BITS parity). No structured CRA feed exists in
   * PHMS yet, so this mirrors the BITS integration environment's placeholder until one lands.
   */
  craLevelLabel(): string {
    return 'Not Available';
  }

  /** Whether a scale renders any radios at all (note-only categories render none). */
  hasScaleOptions(scale: SdmfFactorScale): boolean {
    return sdmfScaleOptions(scale).length > 0;
  }

  readonly canSave = computed(() => this.canEdit() && !this.isSaving());

  setRating(factorCode: string, ratingCode: string): void {
    if (!this.canEdit()) return;
    this.dirty.set(true);
    this.saveIntent = null;
    this.certificationIntent = null;
    this.ratings.update((current) => ({
      ...current,
      [factorCode]: ratingCode,
    }));
  }

  setNote(factorCode: string, value: string): void {
    if (!this.canEdit()) return;
    this.dirty.set(true);
    this.saveIntent = null;
    this.certificationIntent = null;
    this.notes.update((current) => ({ ...current, [factorCode]: value }));
  }

  /**
   * Template-safe note lookup: Record indexing is typed non-optional, so the template cannot
   * use `?? ""` without an NG8102 warning; absent factors read as "".
   */
  noteFor(factorCode: string): string {
    return this.notes()[factorCode] ?? '';
  }

  /** Whole-form save: creates the analysis on first save, replaces it thereafter. */
  save(): void {
    const proceedingId = this._proceedingId();
    if (
      proceedingId === null ||
      proceedingId <= 0 ||
      (!this.canSave() &&
        !(this.uncertain() && this.saveIntent && !this.isSaving()))
    ) {
      return;
    }

    const generation = this.loadGeneration;
    this.isSaving.set(true);
    this.saveError.set(null);

    this.saveIntent ??= {
      key: crypto.randomUUID(),
      payload: this.buildRequest(),
    };
    this.analysisService
      .upsert(proceedingId, this.saveIntent.payload, this.saveIntent.key)
      .pipe(
        catchError((error: unknown) => {
          if (generation !== this.loadGeneration) return of(null);
          this.isSaving.set(false);
          this.saveError.set(messageFromError(error));
          this.uncertain.set(
            !(error instanceof HttpErrorResponse) ||
              error.status === 0 ||
              error.status >= 500,
          );
          if (
            error instanceof HttpErrorResponse &&
            [409, 412].includes(error.status)
          )
            this.stale.set(true);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((saved) => {
        if (generation !== this.loadGeneration) return;
        if (saved && saved.scheduledProceedingId !== proceedingId) {
          this.isSaving.set(false);
          this.loadError.set(
            'The saved analysis does not match this hearing. Reload the analysis before editing.',
          );
          return;
        }
        if (saved) {
          this.isSaving.set(false);
          this.lastSavedAt.set(new Date());
          // Read-back: hydrate from the server's saved record so the surface shows what was stored.
          this.hydrate(saved);
        }
      });
  }

  /**
   * Builds the whole-form request. Rated factors carry their rating + note; note-only entries
   * (a note without a rating — the Introduction / Discordant Information categories, or notes
   * written ahead of a rating) persist too. A factor with neither stays cleared.
   */
  private buildRequest(): SdmfAnalysisUpsertRequest {
    const factors: Record<string, SdmfFactorRatingInput | null> = {};
    const ratings = this.ratings();
    const notes = this.notes();
    for (const factor of this.factors) {
      const ratingCode = (ratings[factor.code] ?? '').trim();
      const note = (notes[factor.code] ?? '').trim();
      if (ratingCode.length === 0 && note.length === 0) {
        continue;
      }
      factors[factor.code] = {
        ratingCode: ratingCode.length > 0 ? ratingCode : null,
        note: note.length > 0 ? note : null,
      };
    }

    return {
      expectedRevision: this.savedAnalysis()?.revision ?? 0,
      factors,
      summaryNarrative: emptyToNull(this.summaryNarrative()),
    };
  }

  setSummary(value: string): void {
    if (this.canEdit()) {
      this.summaryNarrative.set(value);
      this.dirty.set(true);
      this.saveIntent = null;
      this.certificationIntent = null;
    }
  }
  completionIssues(): string[] {
    return sdmfCompletionIssues(this.ratings(), this.summaryNarrative());
  }
  canComplete(): boolean {
    return (
      this.canEdit() &&
      !this.dirty() &&
      !!this.savedAnalysis()?.revision &&
      this.completionIssues().length === 0
    );
  }
  complete(): void {
    if (this.canComplete()) this.certify('complete');
  }
  reopen(): void {
    if (
      this.canAuthor() &&
      this.savedAnalysis()?.isCertified &&
      !this.isSaving()
    )
      this.certify('reopen');
  }
  private certify(operation: 'complete' | 'reopen'): void {
    const id = this._proceedingId(),
      revision = this.savedAnalysis()?.revision;
    if (!id || !revision) return;
    this.certificationIntent ??= {
      key: crypto.randomUUID(),
      operation,
      revision,
    };
    const intent = this.certificationIntent;
    const generation = this.loadGeneration;
    this.isSaving.set(true);
    this.saveError.set(null);
    this.analysisService[intent.operation](id, intent.revision, intent.key)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (analysis) => {
          if (generation !== this.loadGeneration) return;
          this.isSaving.set(false);
          if (analysis.scheduledProceedingId !== id) {
            this.saveError.set('The response does not match this hearing.');
            return;
          }
          this.hydrate(analysis);
        },
        error: (error) => {
          if (generation !== this.loadGeneration) return;
          this.isSaving.set(false);
          this.saveError.set(messageFromError(error));
          this.uncertain.set(
            !(error instanceof HttpErrorResponse) ||
              error.status === 0 ||
              error.status >= 500,
          );
          if (
            error instanceof HttpErrorResponse &&
            [409, 412].includes(error.status)
          )
            this.stale.set(true);
        },
      });
  }
  retryUncertain(): void {
    if (!this.uncertain() || this.isSaving()) return;
    if (this.saveIntent) this.save();
    else if (this.certificationIntent)
      this.certify(this.certificationIntent.operation);
  }
  discardAndReload(): void {
    const id = this._proceedingId();
    if (!id || this.isSaving() || this.uncertain()) return;
    this.load(id);
  }

  retryLoad(): void {
    const id = this._proceedingId();
    if (
      id &&
      !this.dirty() &&
      !this.uncertain() &&
      !this.isLoading() &&
      !this.isSaving()
    )
      this.load(id);
  }

  private resetDraft(): void {
    this.isLoaded.set(false);
    this.isLoading.set(false);
    this.isSaving.set(false);
    this.loadError.set(null);
    this.saveError.set(null);
    this.lastSavedAt.set(null);
    this.savedAnalysis.set(null);
    this.dirty.set(false);
    this.stale.set(false);
    this.uncertain.set(false);
    this.saveIntent = null;
    this.certificationIntent = null;
    this.ratings.set({});
    this.notes.set({});
    this.expanded.set({});
    this.summaryNarrative.set('');
  }

  private load(scheduledProceedingId: number): void {
    const generation = ++this.loadGeneration;
    this.resetDraft();
    this.isLoading.set(true);
    this.analysisService
      .get(scheduledProceedingId)
      .pipe(
        catchError((error: unknown) => {
          if (
            generation === this.loadGeneration &&
            !(error instanceof HttpErrorResponse && error.status === 404)
          ) {
            this.loadError.set(
              'The SDMF analysis could not be loaded. Retry before editing.',
            );
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((analysis) => {
        if (generation !== this.loadGeneration) return;
        this.isLoading.set(false);
        if (
          analysis &&
          analysis.scheduledProceedingId !== scheduledProceedingId
        ) {
          this.loadError.set(
            'The analysis does not match this hearing. Retry before editing.',
          );
          return;
        }
        this.isLoaded.set(!this.loadError());
        if (analysis) this.hydrate(analysis);
      });
  }

  /** Hydrates the form + summary panel from a saved analysis (initial load and post-save read-back). */
  private hydrate(analysis: SdmfAnalysisDto): void {
    this.dirty.set(false);
    this.stale.set(false);
    this.uncertain.set(false);
    this.saveIntent = null;
    this.certificationIntent = null;
    this.savedAnalysis.set(analysis);

    const ratings: Record<string, string> = {};
    const notes: Record<string, string> = {};
    for (const factor of analysis.factors ?? []) {
      if (!factor.factorCode) {
        continue;
      }
      ratings[factor.factorCode] = factor.ratingCode ?? '';
      notes[factor.factorCode] = factor.note ?? '';
    }
    this.ratings.set(ratings);
    this.notes.set(notes);
    // Rows carrying saved narrative open expanded so the notes are immediately visible.
    const expanded: Record<string, boolean> = {};
    for (const [code, note] of Object.entries(notes)) {
      if (note.trim().length > 0) {
        expanded[code] = true;
      }
    }
    this.expanded.set(expanded);
    this.summaryNarrative.set(analysis.summaryNarrative ?? '');
  }
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function messageFromError(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    if (status === 403) {
      return 'You do not have permission to save the SDMF analysis.';
    }
    if (status === 400) {
      return 'The SDMF analysis could not be saved — please check the ratings and try again.';
    }
    if (status === 409 || status === 412)
      return 'A newer analysis is available. Your input is retained. Reconcile it with the current analysis before saving.';
  }
  return 'Save failed. The SDMF analysis was not saved.';
}
